import type { AxiosInstance } from "axios";

export const CAPTURE_CONFIG_SCHEMA_VERSION = 1 as const;
export const CONTRIBUTION_EVENT_SCHEMA_VERSION = 1 as const;
export const CONTRIBUTION_EVENT_VERSION = 1 as const;
export const CONTRIBUTION_CHECKPOINT_SCHEMA_VERSION = 1 as const;

export type ContributionCaptureProfile = "organic_semantic_v1";
export type ContributionOrigin = "human" | "ai" | "automation" | "system";

export interface ContributionCaptureScopesV1 {
    semanticEvents: boolean;
    projectCheckpoints: boolean;
    sourceMediaReferences: boolean;
    renderReferences: boolean;
    contentFields: boolean;
}

export interface ContributionCaptureConfigV1 {
    schemaVersion: typeof CAPTURE_CONFIG_SCHEMA_VERSION;
    enabled: boolean;
    captureConfigId: string;
    consentScopeId: string;
    profile: ContributionCaptureProfile;
    scopes: ContributionCaptureScopesV1;
    allowedProjectIds?: string[];
    effectiveAt?: string;
    expiresAt?: string;
    flushIntervalMs?: number;
    maxBatchSize?: number;
    maxQueueItems?: number;
}

export interface NormalizedContributionCaptureConfigV1 extends ContributionCaptureConfigV1 {
    flushIntervalMs: number;
    maxBatchSize: number;
    maxQueueItems: number;
    allowedProjectIds?: string[];
}

export interface ContributionCompositionSessionContext {
    workspaceId?: string;
    projectId: string;
    compositionId: string;
    role?: string;
    readOnly: boolean;
    appRelease: string;
}

export type ContributionSessionEndReason =
    | "composition_switched"
    | "project_switched"
    | "editor_unmounted"
    | "capture_disabled"
    | "unknown";

export interface ContributionEpisodeCreatePayloadV1
    extends ContributionCompositionSessionContext {
    schemaVersion: 1;
    clientEpisodeId: string;
    sessionId: string;
    profile: ContributionCaptureProfile;
    captureConfigId: string;
    consentScopeId: string;
    startedAt: string;
}

export interface ContributionEpisodeCompletePayloadV1 {
    schemaVersion: 1;
    reason: ContributionSessionEndReason;
    endedAt: string;
}

export type ContributionEpisodeAbandonReason =
    | "capture_disabled"
    | "capture_config_replaced"
    | "capture_authority_changed"
    | "capture_authority_revoked";

export interface ContributionEpisodeAbandonPayloadV1 {
    schemaVersion: 1;
    reason: ContributionEpisodeAbandonReason;
    endedAt: string;
}

export type ContributionJsonPrimitive = string | number | boolean | null;
export type ContributionJsonValue =
    | ContributionJsonPrimitive
    | ContributionJsonValue[]
    | { [key: string]: ContributionJsonValue };

export type ContributionOperationKind =
    | "clip.add"
    | "clip.remove"
    | "clip.update"
    | "script.edit"
    | "canvas.resize"
    | "canvas.background"
    | "transaction.batch"
    | "history.undo"
    | "history.redo";

export type ContributionEntityType = "composition" | "layer" | "clip" | "script" | "canvas";

export interface ContributionOperationTargetRef {
    entityType: ContributionEntityType;
    entityId: string;
}

export interface ContributionCommittedOperationInputV1 {
    operationId?: string;
    operationKind: ContributionOperationKind;
    transactionId?: string;
    targets: ContributionOperationTargetRef[];
    before?: ContributionJsonValue;
    after?: ContributionJsonValue;
    details?: { [key: string]: ContributionJsonValue };
    reversesOperationId?: string;
    reappliesOperationId?: string;
    childOperationIds?: string[];
    origin?: Exclude<ContributionOrigin, "system"> | "system";
}

export interface ContributionCommittedOperationPayloadV1
    extends Omit<ContributionCommittedOperationInputV1, "operationId" | "origin"> {
    operationSchemaVersion: 1;
    operationId: string;
}

export interface ContributionEventPayloadMap {
    "collection.session_started": {
        startedAt: string;
        readOnly: boolean;
        role?: string;
    };
    "project.opened": {
        openedAt: string;
        checkpointExpected: boolean;
    };
    "project.closed": {
        closedAt: string;
        reason: ContributionSessionEndReason;
        checkpointExpected: boolean;
    };
    "collection.session_ended": {
        endedAt: string;
        reason: ContributionSessionEndReason;
    };
    "project.operation_committed": ContributionCommittedOperationPayloadV1;
}

export type ContributionEventType = keyof ContributionEventPayloadMap;
export type ContributionLifecycleEventType = Exclude<ContributionEventType, "project.operation_committed">;

export interface ContributionEventEnvelopeV1<
    T extends ContributionEventType = ContributionEventType,
> {
    schemaVersion: typeof CONTRIBUTION_EVENT_SCHEMA_VERSION;
    eventVersion: typeof CONTRIBUTION_EVENT_VERSION;
    eventId: string;
    eventType: T;
    clientEpisodeId: string;
    sessionId: string;
    producerId: string;
    producerSeq: number;
    occurredAt: string;
    monotonicMs: number;
    appRelease: string;
    captureConfigId: string;
    consentScopeId: string;
    profile: ContributionCaptureProfile;
    workspaceId?: string;
    projectId: string;
    compositionId: string;
    origin: ContributionOrigin;
    payload: ContributionEventPayloadMap[T];
}

export type AnyContributionEventEnvelopeV1 = {
    [T in ContributionEventType]: ContributionEventEnvelopeV1<T>;
}[ContributionEventType];

export type ContributionCheckpointReason =
    | "session_start"
    | "session_end"
    | "before_export"
    | "periodic";

export interface CompositionStructureCheckpointSnapshotV1 {
    schemaVersion: 1;
    compositionId: string;
    display: {
        width: number;
        height: number;
        backgroundColor: string;
    };
    durationSec: number;
    isEmpty: boolean;
    layers: Array<{
        layerId: string;
        clips: Array<{
            clipId: string;
            type: string;
            startSec: number;
            durationSec: number;
            trimmedDurationSec: number;
        }>;
    }>;
}

export interface ContributionCheckpointDraftV1 {
    format: "composition_structure_v1";
    completeness: "structure_only";
    stateHash?: {
        algorithm: "SHA-256";
        value: string;
    };
    snapshot: CompositionStructureCheckpointSnapshotV1;
}

export interface ContributionCheckpointV1 extends ContributionCheckpointDraftV1 {
    schemaVersion: typeof CONTRIBUTION_CHECKPOINT_SCHEMA_VERSION;
    checkpointId: string;
    clientEpisodeId: string;
    sessionId: string;
    reason: ContributionCheckpointReason;
    occurredAt: string;
    monotonicMs: number;
    projectId: string;
    compositionId: string;
}

export type ContributionCaptureHealthStatus =
    | "disabled"
    | "idle"
    | "healthy"
    | "degraded";

export interface ContributionCaptureHealth {
    status: ContributionCaptureHealthStatus;
    enabled: boolean;
    activeEpisodeId: string | null;
    queueDepth: number;
    quarantinedItemCount: number;
    lastErrorCode: string | null;
    lastFlushAt: string | null;
    lastSuccessfulFlushAt: string | null;
}

export interface ContributionHttpClient {
    post: AxiosInstance["post"];
}

export interface ContributionTransportDependencies {
    http: ContributionHttpClient | null;
    getToken: (() => string | null | Promise<string | null>) | null;
}

export interface ContributionCaptureRuntimeOptions extends ContributionTransportDependencies {
    rawConfig: unknown;
    /** Authenticated Firebase UID that owns every durable capture row. */
    ownerUid: string | null;
}
